import React, { useEffect, useState } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import frLocale from 'date-fns/locale/fr';
import {
  TextField,
  Select,
  FormControl,
  MenuItem,
  InputLabel,
  InputAdornment,
} from '@material-ui/core';
import moment from 'moment-timezone';
import { get, isEmpty } from 'lodash';

import messages from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/messages';
import { translate } from 'containers/services/translate';
import { dateStringToLocalDate } from 'containers/IdentificationPage/utils';
import { Config, Listings } from 'containers/AppContainer/typings';
import { JUDGEMENT_ADD_DOCUMENT, LISTINGS } from 'containers/data-structure';
import {
  JudgementADDDocumentType,
  JudgementAddFormName,
} from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/typings';
import { formatPourcentage } from 'containers/services/formatNumberInput';
import { DATE_PICKET_FORMAT, DATE_FORMAT } from 'constants/global';
import { addDaysToDate } from 'containers/common/LegalProceedings/JudgementAdd/Forms/ProcedureInformation/utils';
import { getDateIgnoreTimezone } from 'containers/common/LegalProceedings/utils';
import { useSelector } from 'react-redux';

export interface JudgementAddFormFieldsProps {
  intl: IntlShape;
  listings: Listings;
  judgementAddPronouncing: JudgementADDDocumentType;
  handleSetJudgementAddPronouncing: Function;
  index: number;
  details: any;
  setExpertiseDate?: any;
  expertiseDate?: any;
  judgementAddValidation?: any;
  messageJudgementError?: any;
  hearingDate?: any;
}

const JudgementAddFormFields: React.FC<JudgementAddFormFieldsProps> = (
  props,
): JSX.Element => {
  const [isInputChanged, setIsInputChanged] = useState<boolean>(true);
  const [convictions, setConvictions] = useState<Config[]>([]);
  const [errorDate, setErrorDate] = useState(false);
  /* istanbul ignore next */
  const handleChangeDate = (date: Date | null) => {
    setErrorDate(false);
    const theDate = getDateIgnoreTimezone(date);
    setIsInputChanged(false);
    const judgementAdd = {
      ...props.judgementAddPronouncing,
      [JUDGEMENT_ADD_DOCUMENT.JUDGEMENT_DATE]: moment(theDate as Date).format(
        DATE_FORMAT,
      ),
      [JUDGEMENT_ADD_DOCUMENT.RESPONSABILITY_SHARE]: responsibilityShare,
    };
    if (addDaysToDate(props.hearingDate || '', 0)) {
      if (
        //@ts-ignore
        new Date(addDaysToDate(props.hearingDate || '', 0)) >
          new Date(theDate) ||
        new Date(theDate) > new Date()
      ) {
        setErrorDate(true);
      }
    }
    props.handleSetJudgementAddPronouncing(judgementAdd);
  };
  useEffect(() => {
    let conviction = get(props, `listings.${LISTINGS.CONVICTION}`, []);
    conviction.push(
      conviction.splice(
        conviction.findIndex((arr: { title: string }) => arr.title === 'Autre'),
        1,
      )[0],
    );
    setConvictions(conviction);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [get(props, `listings.${LISTINGS.CONVICTION}`)]);

  const handleChange = (
    event: React.ChangeEvent<{
      name?: string;
      value: unknown;
    }>,
  ) => {
    const { value, name } = event.target;

    const judgementAdd = {
      ...props.judgementAddPronouncing,
    };

    // using Exhaustive switch statements.
    switch (name as JudgementAddFormName) {
      case JUDGEMENT_ADD_DOCUMENT.RESPONSABILITY_SHARE:
        judgementAdd[
          JUDGEMENT_ADD_DOCUMENT.RESPONSABILITY_SHARE
        ] = formatPourcentage(value as string);
        setResponsibilityShare(formatPourcentage(value as string));
        break;

      case JUDGEMENT_ADD_DOCUMENT.CONVICTION_ID:
        judgementAdd[
          JUDGEMENT_ADD_DOCUMENT.CONVICTION_ID
        ] = (value as number).toString();
        break;
    }
    props.handleSetJudgementAddPronouncing(judgementAdd);
  };

  useEffect(() => {
    if (
      new Date(
        get(
          props.judgementAddPronouncing,
          JUDGEMENT_ADD_DOCUMENT.JUDGEMENT_DATE,
        ) as any,
      ) > new Date(props.expertiseDate)
    )
      /* istanbul ignore next */
      handleChangeDate(props.expertiseDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.expertiseDate]);

  const preOpeningResponsabilityShare = useSelector((state: any) =>
    get(state.toJS(), ['recap', 'claimRecap', 'claim', 'responsibilityShare']),
  );

  const [responsibilityShare, setResponsibilityShare] = useState<number | ''>(
    get(
      props.judgementAddPronouncing,
      JUDGEMENT_ADD_DOCUMENT.RESPONSABILITY_SHARE,
      '',
    ) === null
      ? preOpeningResponsabilityShare
      : get(
          props.judgementAddPronouncing,
          JUDGEMENT_ADD_DOCUMENT.RESPONSABILITY_SHARE,
          '',
        ),
  );

  return (
    <main>
      <section className="infoBloc">
        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={frLocale}>
          <KeyboardDatePicker
            autoOk
            variant="inline"
            disableFuture
            invalidDateMessage=""
            format={DATE_PICKET_FORMAT}
            id="form-judgement-date"
            onChange={handleChangeDate}
            name={JUDGEMENT_ADD_DOCUMENT.JUDGEMENT_DATE}
            minDate={addDaysToDate(props.hearingDate || '', 0)}
            value={dateStringToLocalDate(
              get(
                props.judgementAddPronouncing,
                JUDGEMENT_ADD_DOCUMENT.JUDGEMENT_DATE,
              ) as string,
            )}
            label={translate(
              { ...messages.judgementAddFormJudgementDate },
              props,
            )}
            className="field"
            error={
              (!!get(props.judgementAddValidation, [
                JUDGEMENT_ADD_DOCUMENT.JUDGEMENT_DATE,
              ]) &&
                !isInputChanged) ||
              errorDate ||
              get(
                props.judgementAddPronouncing,
                JUDGEMENT_ADD_DOCUMENT.JUDGEMENT_DATE,
              ) === 'Invalid date'
            }
            helperText={
              (!isInputChanged &&
                props.messageJudgementError &&
                props.messageJudgementError[
                  JUDGEMENT_ADD_DOCUMENT.JUDGEMENT_DATE
                ]) ||
              (errorDate &&
                translate(
                  messages.judgementAddFormJudgementDateError,
                  props,
                )) ||
              (get(
                props.judgementAddPronouncing,
                JUDGEMENT_ADD_DOCUMENT.JUDGEMENT_DATE,
              ) === 'Invalid date' &&
                'Merci de saisir le bon format de la date')
            }
            style={{
              top: '2.5px',
            }}
          />
        </MuiPickersUtilsProvider>
        <TextField
          label={translate(
            { ...messages.judgementAddFormResponsibilityShare },
            props,
          )}
          name={JUDGEMENT_ADD_DOCUMENT.RESPONSABILITY_SHARE}
          value={responsibilityShare}
          onChange={handleChange}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {translate(messages.responsibiltySharePercentage, props)}
              </InputAdornment>
            ),
          }}
          type="number"
          className="field"
          data-testid="resp-share"
          error={responsibilityShare === ''}
          helperText={
            responsibilityShare === '' &&
            props.messageJudgementError &&
            props.messageJudgementError[
              JUDGEMENT_ADD_DOCUMENT.RESPONSABILITY_SHARE
            ]
          }
        />
        {String(get(props.details, 'procedureNatureId')) === '3' && (
          <FormControl className="field">
            <InputLabel>
              {translate(messages.judgementAddFormConviction, props)}
            </InputLabel>
            <Select
              name={JUDGEMENT_ADD_DOCUMENT.CONVICTION_ID}
              value={get(
                props.judgementAddPronouncing,
                JUDGEMENT_ADD_DOCUMENT.CONVICTION_ID,
                '',
              )}
              onChange={handleChange}
            >
              {!isEmpty(convictions) &&
                convictions.map((conviction: Config) => {
                  return (
                    <MenuItem
                      value={get(conviction, 'id', 0)}
                      key={get(conviction, 'id', 0)}
                    >
                      {get(conviction, 'title', '')}
                    </MenuItem>
                  );
                })}
            </Select>
          </FormControl>
        )}
      </section>
      {responsibilityShare === 0 && (
        <section>
          <span style={{ marginBottom: '10px', color: 'red' }}>
            {translate(messages.responsibilityShareWarning, props)}
          </span>
        </section>
      )}
    </main>
  );
};

export default injectIntl(JudgementAddFormFields);
